import { IntlShape } from 'react-intl';
import { EucUser } from '../types';
import { SgConnectUser } from './rights';

export const BUS_TOPIC_GLOBALLANGUAGE = 'global.language';
export const BUS_TOPIC_SGCONNECT_ACCESSTOKEN = 'sg-connect.access-token';
export const BUS_TOPIC_SGCONNECT_USERCONNECTION = 'sg-connect.user-connection';
export const BUS_TOPIC_SGCONNECT_USERINFO = 'sg-connect.user-info';
export const BUS_TOPIC_SGCONNECT_GRANTEDSCOPE = 'sg-connect.granted-scope';

type BusEventCallback<T> = (payload: T | undefined) => void;
type SubscriptionHandle = any;

export interface SgWidgetsBus {
  dangerouslyGetCurrentValue<T>(topicName: string): T | undefined;
  subscribe<T>(topicName: string, cb: BusEventCallback<T>): SubscriptionHandle;
  unsubscribe(handle: SubscriptionHandle): void;
}

export function getWidgetBus(): SgWidgetsBus {
  const widgetConfiguration = (window as any).SGWTWidgetConfiguration;
  if (!widgetConfiguration || !widgetConfiguration.bus) {
    throw new Error('No Widgets bus found');
  }
  return widgetConfiguration.bus;
}

export function getWidget<T extends HTMLElement>(tagName: string) {
  return document.querySelector(tagName) as T;
}

export function getAuthorizationHeader(): string | undefined {
  const bus = getWidgetBus();
  if (bus) {
    return bus.dangerouslyGetCurrentValue<string>(BUS_TOPIC_SGCONNECT_ACCESSTOKEN);
  }
  return undefined;
}

export function getAccessToken(): string {
  const bus = getWidgetBus();
  if (bus) {
    const token = bus.dangerouslyGetCurrentValue<string | undefined>(BUS_TOPIC_SGCONNECT_ACCESSTOKEN);
    return token || '';
  }
  return '';
}

export const getUserInfo = (): SgConnectUser | null => {
  const bus = getWidgetBus();
  if (bus) {
    const userInfo = bus.dangerouslyGetCurrentValue<SgConnectUser>(BUS_TOPIC_SGCONNECT_USERINFO);
    return userInfo || null;
  }
  return null;
};

export const getUserInfoAsEucUser = (): EucUser | null => {
  const userInfo = getUserInfo();
  if (userInfo === null) {
    return null;
  }
  return {
    mail: userInfo.mail,
    active: true,
    igg: userInfo.igg,
    department: userInfo.rc_local_sigle,
    entity: userInfo.rc_local_sigle?.split('/')[0] || null,
    firstName: userInfo.first_name,
    lastName: userInfo.last_name,
    manager: null,
    title: null,
    userReferential: null,
    countryIso3: null,
  };
};

/* Display date with 'good' format */
export const displayDate = (date: string | null | undefined): string => {
  if (!date) {
    return 'N/A';
  }
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options as any).format(new Date(date));
};

export const displayDateTime = (date: string | null | undefined): string => {
  if (!date) {
    return 'N/A';
  }
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    time: 'full',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  };
  return new Intl.DateTimeFormat('en-US', options as any).format(new Date(date));
};

export const capitalize = (value?: string | null) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/* Display mail like name and first name */
export const emailFilter = (email?: string | null, onlyName?: boolean): string => {
  if (!email) {
    return '';
  }
  const parts: string[] = email.replace('-ext', '').replace('-itec', '').split('@')[0].split('.');
  if (onlyName && parts[0]) {
    return capitalize(parts[0])!;
  }
  return parts.map((part: string, index: number) => (index === 0 ? capitalize(part) : part.toUpperCase())).join(' ');
};

// Data sensitivity const
export const DATA_SENSITIVITY = ['C1', 'C2', 'C3', 'C4'];

export function generateUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; // tslint:disable-line
    const v = c === 'x' ? r : (r & 0x3) | 0x8; // tslint:disable-line
    return v.toString(16);
  });
}

export const simpleHTML2Text = (html: string): string => html.replace(/<[^>]*>/g, '');

export const existsIntlId = (id: string | null | undefined, intl: IntlShape): boolean => {
  return !!id ? !!intl.messages[id] : false;
};

// return a formatted version of the id when the last char is a space for safe URL
export function getSafeParsedId(id: string) {
  return id.replace(/(\s)$/, '~');
}

// return URL encoded id and restore the space char at the end of the URL
export function getEncodedUriId(id: string) {
  return encodeURIComponent(id.replace(/~$/, ' '));
}

export function isNullOrEmpty(s: string | null | undefined): boolean {
  return s === null || s === undefined || s.trim() === '';
}

export function convertLabelToCamelCaseLabelId(label: string | null | undefined): string | null {
  if (isNullOrEmpty(label)) {
    return null;
  }
  if (label?.length === 1) {
    return label.toLowerCase();
  }

  const tmp = label?.replace(' ', '') ?? '';
  return tmp[0].toLowerCase() + tmp.substring(1);
}

export function convertLabelToKebabCaseLabelId(label: string | null | undefined): string | null {
  if (isNullOrEmpty(label)) {
    return null;
  }

  return label?.toLowerCase().replace(' ', '-') ?? '';
}

export const isActiveGROUPv2 =
  (window as any).globalConfiguration && (window as any).globalConfiguration.isActiveGROUPv2 === true;

export const isVerionControlValid = (url: string): boolean => {
  const valueToTest = (url || '').trim();
  // Accept http://, https://, and any string with \\
  const VERSION_CONTROL_PATTERN = /^(https?:\/\/\S+|\\{2}.+)$/gi;
  return VERSION_CONTROL_PATTERN.test(valueToTest);
};
